import type { Clause } from '@atlaskit/jql-ast/ast/clause';
import { JastBuilder } from '@atlaskit/jql-ast/jast-builder';

const TEXT_FIELD_JQL_TERM = ['textfields', 'text'];

const trimWildcard = (value?: string) => {
	if (!value?.length) {
		return;
	}

	const lastChar = value.charAt(value.length - 1);
	if (lastChar === '*') {
		return value.slice(0, -1);
	}

	return value;
};

/**
 * Extracts the text value from a textfields or text clause in JQL
 * Example: `textfields ~ "bug report"` → `"bug report"`
 */
export const extractTextFieldValueFromJql = (jql: string): string | undefined => {
	try {
		const jast = new JastBuilder().build(jql);

		if (!jast.query || !jast.query.where) {
			return undefined;
		}

		const clause = jast.query.where;
		const value = findTextFieldValue(clause);
		return trimWildcard(value);
	} catch {
		return undefined;
	}
};

/**
 * Recursively searches a clause for textfields terminal clause
 */
const findTextFieldValue = (clause: Clause): string | undefined => {
	// Handle terminal clause
	if ('clauseType' in clause && clause.clauseType === 'terminal') {
		const terminalClause = clause;
		const fieldName = terminalClause.field?.value?.toLowerCase();

		if (TEXT_FIELD_JQL_TERM.includes(fieldName)) {
			// Extract the value from operand
			const operand = terminalClause.operand;
			if (operand && 'value' in operand) {
				return operand.value;
			}
		}
		return undefined;
	}

	// Handle compound clause (AND/OR)
	if ('clauseType' in clause && clause.clauseType === 'compound') {
		const compoundClause = clause;
		const clauses = compoundClause.clauses || [];

		// For AND clauses, look for textfields in any sub-clause
		// For OR clauses, prefer the first textfields value
		for (const subClause of clauses) {
			const value = findTextFieldValue(subClause);
			if (value !== undefined) {
				return value;
			}
		}
	}

	// Handle NOT clause
	if ('clauseType' in clause && clause.clauseType === 'not') {
		const notClause = clause;
		return findTextFieldValue(notClause.clause);
	}

	return undefined;
};
